/**
 * How far this work travels — OND-47, the owner's own control.
 *
 * A RADIO GROUP AND NOT SWITCHES, which is the whole visual difference from
 * `WorkEstatePanel` beside it and says the right thing about both. The estate's
 * two are independent controls, so they are two checkboxes and every
 * combination is reachable. These four are rungs on one scale: exactly one is
 * in force, and a reader must be able to see at a glance which — a set of
 * switches would have invited the question of what two of them on means.
 *
 * FOUR OPTIONS NOW, AND THIS COMPONENT NEEDED NO EDIT TO GROW THE FOURTH.
 * `WORK_VISIBILITIES` has four entries because `ondo.work_visibility` has four
 * since OND-172, and the radios are mapped from the model's list. The promise
 * the previous version of this comment made — "the day the surface exists this
 * component grows the radio with no edit here" — is the one thing about this
 * file that was tested by the fourth setting arriving, and it held.
 *
 * AND ONE CONTROL THAT IS NOT A RADIO, below them: the location opt-in. It is a
 * CHECKBOX and not a fifth rung, because OND-172 requires that making a work
 * public never publish where it is kept — two separate acts, so two separate
 * controls of visibly different kinds, and no arrangement of this group can do
 * one by doing the other.
 *
 * It renders nothing at all for anybody but the owner, because the controller
 * hands it null: `ondo.work_visibility_controls()` returns no row for a
 * non-owner, so there is no panel rather than a panel drawn disabled.
 */
import { WORK_VISIBILITY_COPY } from "./work.constants.ts";
import type { VisibilityPanel } from "./work.model.ts";
import type { WorkVisibility } from "@ondo/shared";

import "./work.css";

type Props = {
  panel: VisibilityPanel | null;
  busy: boolean;
  failed: boolean;
  onChoose: (visibility: WorkVisibility) => void;
  onChooseLocationPublic: (locationPublic: boolean) => void;
};

export function WorkVisibilityPanel({
  panel,
  busy,
  failed,
  onChoose,
  onChooseLocationPublic,
}: Props) {
  if (panel === null) return null;

  return (
    <section
      className="work__panel work-reach"
      aria-labelledby="work-reach-heading"
    >
      <h3 className="work__panel-head" id="work-reach-heading">
        {WORK_VISIBILITY_COPY.heading}
      </h3>
      <p className="work-reach__lead">{WORK_VISIBILITY_COPY.lead}</p>

      {/* A real `radiogroup`, one `name`, so the keyboard moves between the
          four rungs with the arrow keys and a screen reader announces "2 of
          4". `fieldset`/`legend` would duplicate the heading above it, so the
          group is named by that heading instead. The location checkbox below is
          deliberately OUTSIDE this element. */}
      <div
        className="work-reach__options"
        role="radiogroup"
        aria-labelledby="work-reach-heading"
      >
        {panel.options.map((option) => (
          <label className="work-reach__option" key={option.value}>
            <input
              type="radio"
              name="work-reach"
              value={option.value}
              checked={option.selected}
              disabled={busy}
              onChange={() => onChoose(option.value)}
            />
            <span className="work-reach__option-body">
              <span className="work-reach__option-title">{option.title}</span>
              <span className="work-reach__option-note">{option.line}</span>
              {/* The second sentence, when there is one. Only "Region only"
                  ever has it, and only on a work whose location carries no
                  country — so the owner learns the setting discloses nothing
                  BEFORE choosing it rather than after. */}
              {option.note !== null && (
                <span className="work-reach__option-warning">{option.note}</span>
              )}
            </span>
          </label>
        ))}
      </div>

      {/* OND-172's SECOND OPT-IN, and everything about how it is drawn is the
          ticket being held to. It is OUTSIDE the radiogroup — a different
          element type, its own label, its own name — so no keyboard or screen
          reader path treats it as a fifth rung. The warning is rendered while
          the box is UNCHECKED, which is the only moment it can inform a choice;
          once it is on, the line says what is actually being disclosed, and
          "nothing, because this work is not public" is one of the three things
          it can truthfully say. Neither sentence is composed here. */}
      <label className="work-reach__location">
        <input
          type="checkbox"
          checked={panel.location.on}
          disabled={busy}
          onChange={(event) => onChooseLocationPublic(event.target.checked)}
        />
        <span className="work-reach__option-body">
          <span className="work-reach__option-title">{panel.location.label}</span>
          {panel.location.warning !== null && (
            <span className="work-reach__option-warning">
              {panel.location.warning}
            </span>
          )}
          {panel.location.state !== null && (
            <span className="work-reach__option-note">{panel.location.state}</span>
          )}
        </span>
      </label>

      {/* The exception the owner cannot switch off, at every setting rather
          than only at Private — a reader choosing between the four is exactly
          the reader who needs to know one audience is outside the choice. */}
      <p className="work-reach__exception">{panel.exception}</p>

      {/* `role="status"` for progress and `role="alert"` for nothing having
          happened, as on the estate panel. The failure says the setting is
          UNCHANGED, which is true: the controller adopts only what the server
          sent back, so a refused write leaves the radio where it was. */}
      {busy && (
        <p className="work-reach__state" role="status">
          {WORK_VISIBILITY_COPY.saving}
        </p>
      )}
      {failed && !busy && (
        <p className="work-reach__state work-reach__state--failed" role="alert">
          {WORK_VISIBILITY_COPY.failed}
        </p>
      )}
    </section>
  );
}
